import { createContext, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useApolloClient } from '@apollo/client';

import Loading from 'components/Loading';

import {
  isDealerTeam as isDealerTeamType,
  isFleet as isFleetType,
  isDealerGroup as isDealerGroupType,
  isSingleLocation as isSingleLocationType,
} from 'utils/SentinelTeam';
import { useCurrentUserContext } from 'utils/User';
import {
  buildTeamPath,
  getTeamKeyFromPath,
  getTeamKeyFromResourceNumber,
  stripTeamPrefix,
} from 'utils/teamFromUrl';
import ROUTE_NAMES from 'setup/Routing/routeNames';

import useCurrentSentinelTeamQuery from './useCurrentSentinelTeamQuery';

interface SentinelTeamProviderProps {
  children: React.ReactNode;
}

export interface SentinelTeamContextProps extends CurrentSentinelTeam {
  isFleet: boolean;
  isDealerTeam: boolean;
  isDealerGroup: boolean;
  isSingleLocation: boolean;
  isTeamMember: boolean;
  hasSentinelTeam: boolean;
  isTeamEnabled: boolean;
  belongsToOemSegment: boolean;
}

export const SentinelTeamContext = createContext<SentinelTeamContextProps>(
  {} as SentinelTeamContextProps,
);

const SentinelTeamProvider = ({ children }: SentinelTeamProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();

  // Read teamKey from URL params (set by /t/:teamKey route)
  const { teamKey: urlTeamKey } = useParams<{ teamKey?: string }>();

  /* istanbul ignore next -- @preserve */
  const {
    sentinelTeams = [],
    availableTeamKeys = [],
    isSuperUser,
    isManager,
  } = useCurrentUserContext();

  const {
    data: {
      currentUserProfile: {
        user: { sentinelTeam = {} as CurrentSentinelTeam } = {},
      } = {},
    } = {},
    loading,
  } = useCurrentSentinelTeamQuery();

  const {
    teamType,
    id: teamId,
    enabled,
    teamKey: currentTeamKey,
  } = sentinelTeam || {};

  const isFleet = useMemo(() => isFleetType(teamType), [teamType]);
  const isDealerTeam = useMemo(
    () =>
      isDealerTeamType(teamType) ||
      isDealerGroupType(teamType) ||
      isSingleLocationType(teamType),
    [teamType],
  );

  const isDealerGroup = false;
  const isSingleLocation = false;
  const belongsToOemSegment = !!sentinelTeam?.oemSegment;

  const isTeamMember = useMemo(
    () => sentinelTeams.map(({ id }) => id).includes(teamId),
    [sentinelTeams, teamId],
  );

  const hasSentinelTeam = useMemo(() => !!teamId, [teamId]);

  const isTeamEnabled = useMemo(() => !!enabled, [enabled]);

  // Apollo cache reset when team changes in URL
  const prevTeamKeyRef = useRef(urlTeamKey);

  useEffect(() => {
    if (
      prevTeamKeyRef.current &&
      urlTeamKey &&
      prevTeamKeyRef.current !== urlTeamKey
    ) {
      client.resetStore().catch(() => {
        // ignore reset failures to avoid unhandled promise rejections
      });
    }
    prevTeamKeyRef.current = urlTeamKey;
  }, [urlTeamKey, client]);

  // Auto-redirect: bare URL → team-prefixed URL
  // If user visits a bare URL (no /t/:teamKey prefix) and the API returns a team,
  // redirect to /t/:teamKey/<current-path> to establish URL-based context.
  useEffect(() => {
    if (!loading && !urlTeamKey && currentTeamKey) {
      // Guard: if the URL already has a /t/ prefix (e.g. useParams couldn't
      // resolve teamKey because no <Route path="/t/:teamKey"> exists in the
      // tree, such as in tests), skip to avoid an infinite redirect loop.
      if (getTeamKeyFromPath(location.pathname)) return;

      // Don't prefix standalone pages that live outside team scope
      const teamlessRoutes = [
        ROUTE_NAMES.access_denied,
        ROUTE_NAMES.teams,
        ROUTE_NAMES.superadmin.oem_onboard,
        ROUTE_NAMES.superadmin.reports,
      ];
      if (
        location.pathname.startsWith(ROUTE_NAMES.admin.management) ||
        // Reports has routed tabs (/scheduled_reports/:tab), so match by
        // prefix; its old address must not get a team prefix before it redirects.
        location.pathname.startsWith(ROUTE_NAMES.superadmin.reports) ||
        location.pathname.startsWith(ROUTE_NAMES.superadmin.legacyReports) ||
        teamlessRoutes.includes(location.pathname)
      )
        return;

      // Deep links (e.g. email notifications) may arrive without a team
      // prefix. When the path is an appointment/service-event whose number
      // encodes the owning team, route to that team rather than defaulting
      // to the user's current team — otherwise the resource query is scoped
      // to the wrong team and 404s. A key mismatch (or no access) is then
      // resolved by the key-mismatch effect below.
      const innerPath = `${location.pathname}${location.search}${location.hash}`;
      const targetTeamKey =
        getTeamKeyFromResourceNumber(location.pathname) || currentTeamKey;
      navigate(buildTeamPath(targetTeamKey, innerPath), { replace: true });
    }
  }, [
    loading,
    urlTeamKey,
    currentTeamKey,
    isSuperUser,
    isManager,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  // Key mismatch redirect: if URL team key doesn't match API-resolved team key,
  // either the user lacks access (→ access denied) or the key was renamed (→ redirect).
  useEffect(() => {
    if (
      !loading &&
      urlTeamKey &&
      currentTeamKey &&
      urlTeamKey !== currentTeamKey
    ) {
      const matchedTeam = availableTeamKeys.find(
        (mapping: { canonicalKey: string; usedTeamKeys?: string[] }) =>
          mapping.canonicalKey === urlTeamKey ||
          mapping.usedTeamKeys?.includes(urlTeamKey),
      );

      if (!matchedTeam) {
        navigate(ROUTE_NAMES.access_denied, {
          replace: true,
        });
        return;
      }
      const innerPath = stripTeamPrefix(
        `${location.pathname}${location.search}${location.hash}`,
      );
      navigate(buildTeamPath(matchedTeam.canonicalKey, innerPath), {
        replace: true,
      });
    }
  }, [
    loading,
    urlTeamKey,
    currentTeamKey,
    availableTeamKeys,
    sentinelTeams,
    location,
    navigate,
  ]);

  // Redirect to teamUrl if it's defined and host differs from current host.
  // Skip if only the scheme (http vs https) differs to avoid loops under forced HTTPS.
  useEffect(() => {
    const teamUrl = sentinelTeam?.teamUrl;

    if (teamUrl && !loading) {
      try {
        const url = new URL(teamUrl);
        const current = new URL(window.location.href);
        const targetHost = url.hostname.toLowerCase();
        const currentHost = current.hostname.toLowerCase();
        const schemeDiffOnly =
          targetHost === currentHost && url.protocol !== current.protocol;

        if (!schemeDiffOnly && targetHost !== currentHost) {
          const targetUrl = `${url.protocol}//${url.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
          window.location.href = targetUrl;
        }
      } catch (error) {
        // Invalid URL, ignore and continue
      }
    }
  }, [sentinelTeam, loading]);

  const values = useMemo(
    () => ({
      ...sentinelTeam,
      isFleet,
      isDealerTeam,
      isDealerGroup,
      isSingleLocation,
      isTeamMember,
      belongsToOemSegment,
      hasSentinelTeam,
      isTeamEnabled,
    }),
    [
      sentinelTeam,
      isFleet,
      isDealerTeam,
      isDealerGroup,
      isSingleLocation,
      isTeamMember,
      belongsToOemSegment,
      hasSentinelTeam,
      isTeamEnabled,
    ],
  );

  const isSwitchingTeam =
    !!urlTeamKey && !!currentTeamKey && urlTeamKey !== currentTeamKey;

  if (loading || isSwitchingTeam) {
    return <Loading />;
  }

  return (
    <SentinelTeamContext.Provider value={values}>
      {children}
    </SentinelTeamContext.Provider>
  );
};

SentinelTeamProvider.displayName = 'SentinelTeamProvider';

SentinelTeamProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SentinelTeamProvider;
